import { Component } from 'preact';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_MUTED } from '../../lib/medievalTheme';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { formatRank } from '../../utils/arena/rankUtils';
import { getLeaderboard, getSelfLeaderboardRank } from '../../utils/arena/matchmakingApi';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { k, t } from '../../localization/t';
import { isFreePlay } from '../../utils/freePlay';

// Phone leaderboard. Full-screen overlay over the mobile hub: back
// button + mode tabs up top, a natively scrolling Top 100 list, and a
// pinned "your rank" bar when you're not inside the visible slice.
// Mobile-appropriate scope on purpose: no tier filter, no search, no
// virtualized infinite ladder (the desktop hub keeps those).

// Same palette the desktop ladder uses (duplicated there and in three
// sibling screens; the map is tiny and tier names are stable).
const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

const MODES = [
  ['ranked', k('Ranked')],
  ['casual', k('Casual')],
  ['limited', k('Limited')],
  ['sandbox', k('Sandbox')],
];

// Shared with the desktop hub so both remember the same last-open tab.
const MODE_KEY = 'valkenhall:leaderboardMode';
const VALID_MODES = new Set(MODES.map(([m]) => m));
// Free play has no Sandbox ladder.
const visibleModes = () => (isFreePlay() ? MODES.filter(([m]) => m !== 'sandbox') : MODES);
const readMode = () => {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    return VALID_MODES.has(stored) && !(stored === 'sandbox' && isFreePlay()) ? stored : 'ranked';
  } catch { return 'ranked'; }
};

// Two server pages; the backend's page size is 50.
const PAGE = 50;
const TOP_N = 100;

export default class MobileLeaderboard extends Component {
  state = {
    mode: readMode(),
    rows: [],
    total: 0,
    loading: true,
    selfRank: null,
  };

  componentDidMount() {
    this.load(this.state.mode);
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  load = async (mode) => {
    this._epoch = (this._epoch || 0) + 1;
    const epoch = this._epoch;
    this.setState({ loading: true, rows: [], total: 0, selfRank: null });

    getSelfLeaderboardRank({ mode })
      .then((me) => {
        if (this._unmounted || epoch !== this._epoch) return;
        this.setState({ selfRank: me });
      })
      .catch(() => {});

    try {
      const first = await getLeaderboard({ offset: 0, limit: PAGE, mode });
      if (this._unmounted || epoch !== this._epoch) return;
      let rows = Array.isArray(first?.rows) ? first.rows : [];
      const total = typeof first?.total === 'number' ? first.total : rows.length;
      if (rows.length === PAGE && total > PAGE) {
        const second = await getLeaderboard({ offset: PAGE, limit: PAGE, mode });
        if (this._unmounted || epoch !== this._epoch) return;
        rows = rows.concat(Array.isArray(second?.rows) ? second.rows : []);
      }
      this.setState({ rows: rows.slice(0, TOP_N), total, loading: false });
    } catch {
      if (this._unmounted || epoch !== this._epoch) return;
      this.setState({ loading: false });
    }
  };

  setMode = (mode) => {
    if (mode === this.state.mode) return;
    playUI(UI.CLICK);
    try { localStorage.setItem(MODE_KEY, mode); } catch {}
    this.setState({ mode });
    this.load(mode);
  };

  handleBack = () => {
    playUI(UI.CANCEL);
    this.props.onClose?.();
  };

  renderRow = (player, i) => {
    const rank = i + 1;
    const isMe = player.name === this.props.profile?.name;
    const isRanked = this.state.mode === 'ranked';
    const avatarUrl = resolveAvatarUrl(player, this.props.sorceryCards);
    const tierHex = TIER_HEX[player.tier] || '#A6A09B';
    return (
      <button
        key={player.id || rank}
        type="button"
        className="flex items-center gap-2.5 w-full text-left px-3 active:brightness-125"
        style={{
          minHeight: '2.75rem',
          background: isMe ? 'linear-gradient(90deg, rgba(212,168,67,0.22) 0%, rgba(212,168,67,0.06) 60%, transparent 100%)' : 'transparent',
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}
        onClick={() => {
          playUI(UI.CLICK);
          if (player.id && this.props.onViewProfile) this.props.onViewProfile(player.id);
          else toast(t('Player profiles are coming to mobile soon.'));
        }}
      >
        <span
          className="shrink-0 text-right font-bold tabular-nums text-sm"
          style={{ width: '2rem', color: rank <= 3 ? ACCENT_GOLD : TEXT_MUTED }}
        >
          {rank}
        </span>
        {avatarUrl ? (
          <AvatarImage
            src={avatarUrl}
            className="shrink-0 rounded-full"
            style={{ width: '1.75rem', height: '1.75rem', border: `1px solid ${GOLD} 0.25)` }}
            loading="lazy"
          />
        ) : (
          <div
            className="shrink-0 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ width: '1.75rem', height: '1.75rem', background: `${GOLD} 0.1)`, border: `1px solid ${GOLD} 0.25)`, color: TEXT_PRIMARY }}
          >
            {(player.name || '?')[0].toUpperCase()}
          </div>
        )}
        <span
          className="flex-1 min-w-0 truncate text-sm font-semibold"
          style={{ color: isMe ? ACCENT_GOLD : TEXT_PRIMARY }}
        >
          {player.name}
        </span>
        {isRanked ? (
          <span className="shrink-0 text-xs font-bold" style={{ color: tierHex }}>
            {formatRank(player.tier, player.division)}
          </span>
        ) : (
          <span className="shrink-0 text-xs font-bold tabular-nums" style={{ color: ACCENT_GOLD }}>
            {t('{wins}W · {losses}L', { wins: Number(player.modeWins ?? 0).toLocaleString(), losses: Number(player.modeLosses ?? 0).toLocaleString() })}
          </span>
        )}
      </button>
    );
  };

  render() {
    const { mode, rows, loading, selfRank } = this.state;
    // The pinned self-rank bar only earns its row when you exist on this
    // ladder and sit below the visible slice.
    const showSelfBar = selfRank?.rank && selfRank.rank > rows.length;

    return (
      <div className="absolute inset-0 z-30 flex flex-col phone-safe-pad" style={{ background: 'rgba(8,6,4,0.97)' }}>
        {/* Header */}
        <div className="flex items-center gap-2 px-2 pt-2 pb-1.5" style={{ borderBottom: `1px solid ${GOLD} 0.18)` }}>
          <button
            type="button"
            aria-label={t('Back')}
            className="flex items-center justify-center shrink-0 active:scale-[0.92] transition-transform duration-100"
            style={{ width: '2.75rem', height: '2.75rem' }}
            onClick={this.handleBack}
          >
            <ChevronLeft size={24} color={TEXT_PRIMARY} />
          </button>
          <span className="arena-heading text-lg font-bold shrink-0" style={{ color: TEXT_PRIMARY }}>
            {t('Leaderboard')}
          </span>
          <div className="flex-1" />
          {/* Mode tabs */}
          <div className="flex items-center gap-1 pr-1">
            {visibleModes().map(([value, label]) => {
              const active = mode === value;
              return (
                <button
                  key={value}
                  type="button"
                  className="text-sm font-semibold px-3 transition-transform duration-100 active:translate-y-px"
                  style={{
                    minHeight: '2.25rem',
                    borderRadius: 'var(--radius-full)',
                    border: active ? `1px solid ${GOLD} 0.6)` : '1px solid rgba(255,255,255,0.12)',
                    background: active ? 'linear-gradient(180deg, rgba(60,46,24,0.9) 0%, rgba(34,26,14,0.95) 100%)' : 'transparent',
                    color: active ? ACCENT_GOLD : TEXT_MUTED,
                  }}
                  onClick={() => this.setMode(value)}
                >
                  {t(label)}
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-sm" style={{ color: TEXT_MUTED }}>
              {t('Summoning the ladder…')}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm" style={{ color: TEXT_MUTED }}>
              {t('No entries on this ladder yet.')}
            </div>
          ) : (
            rows.map(this.renderRow)
          )}
        </div>

        {/* Pinned self rank */}
        {showSelfBar ? (
          <div
            className="flex items-center gap-2.5 px-3"
            style={{
              minHeight: '2.75rem',
              borderTop: `1px solid ${GOLD} 0.35)`,
              background: 'linear-gradient(180deg, rgba(38,30,18,0.98) 0%, rgba(22,16,9,0.98) 100%)',
            }}
          >
            <span className="shrink-0 text-right font-bold tabular-nums text-sm" style={{ width: '2rem', color: ACCENT_GOLD }}>
              {selfRank.rank}
            </span>
            <span className="flex-1 min-w-0 truncate text-sm font-semibold" style={{ color: ACCENT_GOLD }}>
              {t('{name} (you)', { name: this.props.profile?.name })}
            </span>
            {/* /leaderboard/me returns only {rank, total}; own tier comes
                from the profile the hub already holds. */}
            {mode === 'ranked' && this.props.profile?.rank?.tier ? (
              <span className="shrink-0 text-xs font-bold" style={{ color: TIER_HEX[this.props.profile.rank.tier] || TEXT_MUTED }}>
                {formatRank(this.props.profile.rank.tier, this.props.profile.rank.division)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}
